import { Pencil, RotateCcw, Search, Trash2, X } from 'lucide-react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { hospitalService } from '../api/services/hospital.service'
import { PermissionGate } from '../components/auth/PermissionGate'
import { AppShell } from '../components/layout/AppShell'
import { Button } from '../components/ui/button'
import { FieldLabel, FormRequiredLegend } from '../components/ui/field-label'
import { Input } from '../components/ui/input'
import { ListPagination } from '../components/ui/list-pagination'
import { SelectPicker, type SelectOption } from '../components/ui/select-picker'
import { SoftDeleteConfirmDialog } from '../components/ui/soft-delete-confirm-dialog'
import { DEFAULT_PAGE_SIZE, resolveListPage, type PaginationMeta } from '../lib/api-list'
import { IconActionButton, TableIconActions } from '../components/ui/table-icon-actions'
import { useAuth } from '../context/AuthContext'
import type { Hospital, HospitalFormPayload } from '../types/hospital'

type ActiveFilter = 'all' | 'active' | 'disabled'

const ACTIVE_OPTIONS: SelectOption[] = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'disabled', label: 'Inactive' },
]

const hospitalSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  address: z.string().optional(),
  is_active: z.boolean(),
})

type HospitalFormValues = z.infer<typeof hospitalSchema>

const emptyForm: HospitalFormValues = {
  name: '',
  address: '',
  is_active: true,
}

export function HospitalsPage() {
  const { can } = useAuth()
  const [hospitals, setHospitals] = useState<Hospital[]>([])
  const [hasLoaded, setHasLoaded] = useState(false)
  const [withTrashed, setWithTrashed] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>('all')
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<Hospital | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<Hospital | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState<number>(DEFAULT_PAGE_SIZE)
  const [meta, setMeta] = useState<PaginationMeta | null>(null)

  const form = useForm<HospitalFormValues>({
    resolver: zodResolver(hospitalSchema),
    defaultValues: emptyForm,
  })

  const activeFilterCount = useMemo(() => {
    let count = 0
    if (search.trim()) count += 1
    if (activeFilter !== 'all') count += 1
    if (withTrashed) count += 1
    return count
  }, [activeFilter, search, withTrashed])

  const clearFilters = () => {
    setSearch('')
    setSearchInput('')
    setActiveFilter('all')
    setWithTrashed(false)
  }

  const loadHospitals = useCallback(async () => {
    try {
      const response = await hospitalService.list({
        page,
        per_page: perPage,
        only_trashed: withTrashed || undefined,
        search: search.trim() || undefined,
        is_active: activeFilter === 'all' ? undefined : activeFilter === 'active',
      })
      const { items, meta: nextMeta } = resolveListPage(response, page, perPage)
      setHospitals(items)
      setMeta(nextMeta)
    } catch {
      setHospitals([])
      setMeta(null)
    } finally {
      setHasLoaded(true)
    }
  }, [activeFilter, page, perPage, search, withTrashed])

  useEffect(() => {
    void loadHospitals()
  }, [loadHospitals])

  useEffect(() => {
    setPage(1)
  }, [activeFilter, search, withTrashed])

  const openCreate = () => {
    setEditing(null)
    form.reset(emptyForm)
    setFormOpen(true)
  }

  const openEdit = (hospital: Hospital) => {
    setEditing(hospital)
    form.reset({
      name: hospital.name,
      address: hospital.address ?? '',
      is_active: hospital.is_active,
    })
    setFormOpen(true)
  }

  const closeForm = () => {
    setFormOpen(false)
    setEditing(null)
    form.reset(emptyForm)
  }

  const onSubmit = async (values: HospitalFormValues) => {
    setSubmitting(true)

    const payload: HospitalFormPayload = {
      name: values.name,
      address: values.address || undefined,
      is_active: values.is_active,
    }

    try {
      if (editing) {
        await hospitalService.update(editing.id, payload)
      } else {
        await hospitalService.create(payload)
      }

      closeForm()
      await loadHospitals()
    } catch {
      // handled globally
    } finally {
      setSubmitting(false)
    }
  }

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) {
      return
    }

    setDeleting(true)

    try {
      await hospitalService.remove(deleteTarget.id)
      setDeleteTarget(null)
      await loadHospitals()
    } catch {
      // handled globally
    } finally {
      setDeleting(false)
    }
  }

  const handleRestore = async (hospital: Hospital) => {
    try {
      await hospitalService.restore(hospital.id)
      await loadHospitals()
    } catch {
      // handled globally
    }
  }

  return (
    <AppShell>
      <div className="mx-auto max-w-6xl px-2 py-2">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl font-bold">Hospitals</h1>
            <p className="mt-1 text-sm text-ink-600">
              Manage hospital reference data for doctor signup and profiles.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-ink-700">
              <input
                type="checkbox"
                className="h-4 w-4 accent-green-600"
                checked={withTrashed}
                onChange={(event) => setWithTrashed(event.target.checked)}
              />
              Show Deleted
            </label>

            <PermissionGate permission="hospitals-create">
              <Button onClick={openCreate}>Add Hospital</Button>
            </PermissionGate>
          </div>
        </div>

        <section className="mb-6 rounded-xl border border-parch-100 bg-white p-4 shadow-card sm:p-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold text-ink-900">Filters</h2>
              {activeFilterCount > 0 && (
                <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-green-100 px-1.5 text-xs font-semibold text-green-700">
                  {activeFilterCount}
                </span>
              )}
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-end gap-2">
            <div className="min-w-[12rem] flex-1 basis-0">
              <FieldLabel>Name</FieldLabel>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400" />
                <Input
                  className="pl-9"
                  placeholder="Search hospital name…"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      setSearch(searchInput.trim())
                    }
                  }}
                />
              </div>
            </div>
            <div className="min-w-[12rem] flex-1 basis-0">
              <FieldLabel>Status</FieldLabel>
              <SelectPicker
                options={ACTIVE_OPTIONS}
                value={activeFilter}
                onChange={(value) => setActiveFilter(value as ActiveFilter)}
                placeholder="All"
              />
            </div>
            <Button onClick={() => setSearch(searchInput.trim())}>Search</Button>
            {activeFilterCount > 0 && (
              <Button variant="secondary" onClick={clearFilters}>
                <X className="h-4 w-4" />
                Clear
              </Button>
            )}
          </div>
        </section>

        <section className="overflow-hidden rounded-lg border border-parch-100 bg-white shadow-card">
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="border-b border-parch-100 bg-parch-50 text-ink-600">
                <tr>
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium">Address</th>
                  <th className="px-4 py-3 font-medium">Active</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {hasLoaded && hospitals.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-6 text-sm text-ink-500">
                      No hospitals found.
                    </td>
                  </tr>
                ) : (
                  hospitals.map((hospital) => (
                    <tr key={hospital.id} className="border-b border-parch-50 last:border-0">
                      <td className="px-4 py-3">{hospital.name}</td>
                      <td className="px-4 py-3">{hospital.address ?? '—'}</td>
                      <td className="px-4 py-3">{hospital.is_active ? 'Yes' : 'No'}</td>
                      <td className="px-4 py-3">
                        {hospital.deleted_at ? (
                          <span className="text-danger">Deleted</span>
                        ) : (
                          <span className="text-green-700">Active</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <TableIconActions>
                          {can('hospitals-update') && !hospital.deleted_at && (
                            <IconActionButton
                              icon={Pencil}
                              label="Edit"
                              onClick={() => openEdit(hospital)}
                            />
                          )}
                          {can('hospitals-delete') && !hospital.deleted_at && (
                            <IconActionButton
                              icon={Trash2}
                              label="Delete"
                              variant="destructive"
                              onClick={() => setDeleteTarget(hospital)}
                            />
                          )}
                          {can('hospitals-restore') && hospital.deleted_at && (
                            <IconActionButton
                              icon={RotateCcw}
                              label="Restore"
                              onClick={() => void handleRestore(hospital)}
                            />
                          )}
                        </TableIconActions>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>

        <ListPagination
          meta={meta}
          perPage={perPage}
          onPageChange={setPage}
          onPerPageChange={(n) => {
            setPerPage(n)
            setPage(1)
          }}
        />

        <SoftDeleteConfirmDialog
          open={deleteTarget !== null}
          entityLabel="hospital"
          itemName={deleteTarget?.name ?? ''}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => void handleDeleteConfirm()}
          confirming={deleting}
        />

        {formOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-card">
              <h2 className="font-display text-xl font-bold">
                {editing ? 'Edit Hospital' : 'Create Hospital'}
              </h2>

              <form className="mt-6 space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
                <FormRequiredLegend />
                <div>
                  <FieldLabel required>Name</FieldLabel>
                  <Input {...form.register('name')} />
                  {form.formState.errors.name && (
                    <p className="mt-1 text-xs text-danger">{form.formState.errors.name.message}</p>
                  )}
                </div>

                <div>
                  <FieldLabel optional>Address</FieldLabel>
                  <Input {...form.register('address')} />
                </div>

                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" {...form.register('is_active')} />
                  Active
                </label>

                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="secondary" onClick={closeForm}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting}>
                    {submitting ? 'Saving…' : editing ? 'Save Changes' : 'Create Hospital'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  )
}
